/**
 * Adapter objects for external developer tools integrated by DevPill.
 *
 * The DevProxy adapter uses API-based discovery (`/_service` and
 * `/api/routes/<service>`). The ReactScan and Next.js adapters use
 * DOM selectors and shadow DOM traversal. All adapters provide a
 * consistent interface for discovery, visibility toggling, and data
 * extraction.
 */

import {
  parseDevProxyRoutesPayload,
  parseServiceAndWorktree,
} from '@fpo3/ts-commons-core/dev-proxy-url';

export interface WorktreeOption {
  value: string;
  label: string;
  port: string | null;
  selected: boolean;
  disabled: boolean;
}

export type PillPosition = 'top-right' | 'bottom-right' | 'hidden';

/** Named constants for Next.js error indicator RGB thresholds. */
const ERROR_RED_MIN = 180;
const ERROR_GREEN_MAX = 100;
const ERROR_BLUE_MAX = 100;

/**
 * Parses an RGB/RGBA background-color string and returns whether it indicates
 * the Next.js error state (red indicator). Tested against Next.js 15.x/16.x.
 */
export function isErrorIndicatorColor(backgroundColor: string): boolean {
  const match = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return false;
  return (
    Number(match[1]) > ERROR_RED_MIN &&
    Number(match[2]) < ERROR_GREEN_MAX &&
    Number(match[3]) < ERROR_BLUE_MAX
  );
}

// ── DevProxy Adapter ──
// Integrates with dev-proxy via API endpoints: /_service and /api/routes/<service>

export interface DevProxyAdapter {
  /** Async discovery — fetches /_service at host root */
  discover(): Promise<boolean>;
  /** No-op since there's no injected overlay to hide anymore */
  hide(): void;
  /** Fetches worktree options from /api/routes/<service> */
  getOptions(): Promise<WorktreeOption[]>;
  /** Rewrites URL to switch worktree */
  switchWorktree(value: string): void;
  /** Returns the current worktree name (parsed from URL) */
  getCurrentWorktree(): string | null;
  /** Returns the service name */
  getServiceName(): string | null;
  /** Returns true when running on a cloud node (FPO3_CLOUD_NODE is set on the dev-proxy server) */
  isCloudNode(): boolean;
}

/** Creates the dev-proxy adapter that discovers the proxied service via `/_service` and provides worktree switching. */
export function createDevProxyAdapter(): DevProxyAdapter {
  let proxied = false;
  let serviceName: string | null = null;
  let currentWorktree: string | null = null;
  let cloudNode = false;

  return {
    async discover(): Promise<boolean> {
      try {
        const response = await globalThis.fetch('/_service');
        if (!response.ok) return false;
        const data = await response.json();
        if (data?.proxied === true) {
          proxied = true;
          cloudNode = !!data.cloudNode;
          const parsed = parseServiceAndWorktree(window.location.pathname);
          if (parsed) {
            serviceName = parsed.service;
            currentWorktree = parsed.worktree;
          }
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    hide() {
      // No-op — no injected overlay to hide
    },
    async getOptions(): Promise<WorktreeOption[]> {
      if (!proxied || !serviceName) return [];
      try {
        const response = await globalThis.fetch(`/api/routes/${encodeURIComponent(serviceName)}`);
        if (!response.ok) return [];
        const data = await response.json();
        const parsed = parseDevProxyRoutesPayload(data);
        if (parsed == null) return [];
        return parsed.worktrees.map((wt) => ({
          value: wt.worktree,
          label: wt.worktree,
          port: String(wt.port),
          selected: wt.worktree === currentWorktree,
          disabled: false,
        }));
      } catch {
        return [];
      }
    },
    switchWorktree(value: string) {
      if (!serviceName) return;
      // Replace /<service>/<current-worktree> with /<service>/<new-worktree>
      const path = window.location.pathname.replace(/^\/[^/]+\/[^/]+/, '');
      window.location.href = `/${encodeURIComponent(serviceName)}/${encodeURIComponent(value)}${path || '/'}${window.location.search}${window.location.hash}`;
    },
    getCurrentWorktree() {
      return currentWorktree;
    },
    getServiceName() {
      return serviceName;
    },
    isCloudNode() {
      return cloudNode;
    },
  };
}

// ── ReactScan Adapter ──
// Integrates with react-scan: #react-scan-root → shadowRoot → #react-scan-toolbar, #react-scan-notifications

export interface ReactScanAdapter {
  discover(): boolean;
  hide(): void;
  show(): void;
  isVisible(): boolean;
  /**
   * Read render count from react-scan notifications.
   * NOTE: Depends on react-scan's internal DOM structure (tested against 0.3.x).
   * If react-scan changes its notification format, this returns null.
   */
  getRenderCount(): number | null;
}

/** Creates the react-scan adapter that locates the shadow-DOM toolbar and exposes visibility toggling and render count. */
export function createReactScanAdapter(): ReactScanAdapter {
  let toolbar: HTMLElement | null = null;
  let shadow: ShadowRoot | null = null;

  return {
    discover() {
      const root = document.getElementById('react-scan-root');
      shadow = root?.shadowRoot ?? null;
      toolbar = shadow?.getElementById('react-scan-toolbar') ?? null;
      return toolbar != null;
    },
    hide() {
      if (toolbar) toolbar.style.display = 'none';
    },
    show() {
      if (toolbar) toolbar.style.display = '';
    },
    isVisible() {
      return toolbar ? toolbar.style.display !== 'none' : false;
    },
    getRenderCount() {
      if (!shadow) return null;
      const notifications = shadow.getElementById('react-scan-notifications');
      if (!notifications) return null;
      for (const child of notifications.querySelectorAll('div')) {
        const text = child.textContent?.trim() ?? '';
        if (/^\d+$/.test(text)) {
          return Number.parseInt(text, 10);
        }
      }
      return null;
    },
  };
}

// ── Next.js Adapter ──
// Integrates with Next.js devtools: nextjs-portal → shadowRoot → #devtools-indicator

export interface NextjsAdapter {
  discover(): boolean;
  hide(): void;
  show(): void;
  isVisible(): boolean;
  getPosition(): PillPosition;
  hasError(): boolean;
}

/** Creates the Next.js adapter that hides the native devtools indicator and exposes error state for the pill badge. */
export function createNextjsAdapter(): NextjsAdapter {
  let indicator: HTMLElement | null = null;

  return {
    discover() {
      const portal = document.querySelector('nextjs-portal');
      indicator = portal?.shadowRoot?.getElementById('devtools-indicator') as HTMLElement | null;
      return indicator != null;
    },
    hide() {
      if (indicator) indicator.style.display = 'none';
    },
    show() {
      if (indicator) indicator.style.display = '';
    },
    isVisible() {
      return indicator ? indicator.style.display !== 'none' : false;
    },
    getPosition(): PillPosition {
      if (!indicator) return 'hidden';
      const rect = indicator.getBoundingClientRect();
      return rect.top < window.innerHeight / 2 ? 'top-right' : 'bottom-right';
    },
    hasError() {
      if (!indicator) return false;
      return isErrorIndicatorColor(getComputedStyle(indicator).backgroundColor);
    },
  };
}
